import { useState, useEffect } from 'react'
import api from '../utils/api'
import { Users, AlertCircle, UserPlus, Trash2, X, RefreshCw, Copy, Check, KeyRound } from 'lucide-react'
import { clsx } from 'clsx'

/** Generate a random 12-character password with upper, lower, digit, special. */
function generateTempPassword(): string {
    const upper   = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
    const lower   = 'abcdefghjkmnpqrstuvwxyz'
    const digits  = '23456789'
    const special = '!@#$%^&*'
    const all     = upper + lower + digits + special
    const arr     = [
        upper[Math.floor(Math.random() * upper.length)],
        lower[Math.floor(Math.random() * lower.length)],
        digits[Math.floor(Math.random() * digits.length)],
        special[Math.floor(Math.random() * special.length)],
        ...Array.from({ length: 8 }, () => all[Math.floor(Math.random() * all.length)]),
    ]
    // shuffle
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]]
    }
    return arr.join('')
}

type Role = 'owner' | 'admin' | 'team_member'

interface User {
    id: string
    email: string
    role: Role | string
    subscription_status: string
    subscription_tier: string
    created_at: string
}

const ROLES: { value: Role; label: string; color: string }[] = [
    { value: 'owner',       label: 'Owner',       color: 'bg-red-50 text-red-700' },
    { value: 'admin',       label: 'Admin',       color: 'bg-purple-50 text-purple-700' },
    { value: 'team_member', label: 'Team Member', color: 'bg-blue-50 text-blue-700' },
]

/** Map legacy API/DB roles onto the three-seat vocabulary for the dropdown. */
function normalizeRoleForUi(role: string): Role {
    if (role === 'owner' || role === 'admin' || role === 'team_member') return role
    return 'team_member'
}

const ROLE_COLORS: Record<string, string> = Object.fromEntries(
    ROLES.map(r => [r.value, r.color])
)

const TIER_COLORS: Record<string, string> = {
    free: 'bg-gray-100 text-gray-700',
    starter: 'bg-sky-100 text-sky-700',
    professional: 'bg-indigo-100 text-indigo-700',
    enterprise: 'bg-amber-100 text-amber-800',
}

function formatTierLabel(tier: string): string {
    if (!tier) return 'Free'
    return tier.charAt(0).toUpperCase() + tier.slice(1)
}

interface CreateUserForm {
    email: string
    temp_password: string
    role: Role
    subscription_tier: string
}

export const UserManagement = () => {
    const [users, setUsers] = useState<User[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [updatingId, setUpdatingId] = useState<string | null>(null)
    const [deletingId, setDeletingId] = useState<string | null>(null)
    const [showCreateModal, setShowCreateModal] = useState(false)
    const [createForm, setCreateForm] = useState<CreateUserForm>({
        email: '',
        temp_password: generateTempPassword(),
        role: 'team_member',
        subscription_tier: 'professional',
    })
    const [creating, setCreating] = useState(false)
    const [createError, setCreateError] = useState<string | null>(null)
    const [confirmDelete, setConfirmDelete] = useState<User | null>(null)
    const [copiedPassword, setCopiedPassword] = useState(false)
    const [resetTarget, setResetTarget] = useState<User | null>(null)
    const [resetTempPassword, setResetTempPassword] = useState('')
    const [resetting, setResetting] = useState(false)
    const [resetError, setResetError] = useState<string | null>(null)
    const [copiedResetPassword, setCopiedResetPassword] = useState(false)

    useEffect(() => { fetchUsers() }, [])

    const fetchUsers = async () => {
        try {
            setLoading(true)
            const response = await api.get('/api/v1/users')
            setUsers(response.data)
            setError(null)
        } catch (err: any) {
            console.error('Failed to fetch users:', err)
            setError('Failed to load users. You might not have permission.')
        } finally {
            setLoading(false)
        }
    }

    const handleRoleChange = async (userId: string, newRole: Role) => {
        try {
            setUpdatingId(userId)
            await api.put(`/api/v1/users/${userId}/role`, { role: newRole })
            setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u))
        } catch (err: any) {
            console.error('Failed to update role:', err)
            setError('Failed to update user role.')
        } finally {
            setUpdatingId(null)
        }
    }

    const handleCreateUser = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!createForm.email.trim()) return
        try {
            setCreating(true)
            setCreateError(null)
            const response = await api.post('/api/v1/users', createForm)
            setUsers([response.data, ...users])
            setShowCreateModal(false)
            setCreateForm({ email: '', temp_password: generateTempPassword(), role: 'team_member', subscription_tier: 'professional' })
        } catch (err: any) {
            const data = err?.response?.data
            const msg =
                data?.detail ||
                data?.message ||
                data?.details?.[0]?.msg ||
                'Failed to create user.'
            setCreateError(msg)
        } finally {
            setCreating(false)
        }
    }

    const copyPassword = async () => {
        try {
            await navigator.clipboard.writeText(createForm.temp_password)
            setCopiedPassword(true)
            setTimeout(() => setCopiedPassword(false), 2000)
        } catch {
            // fallback: select the input text
        }
    }

    const handleDeleteUser = async (user: User) => {
        try {
            setDeletingId(user.id)
            await api.delete(`/api/v1/users/${user.id}`)
            setUsers(users.filter(u => u.id !== user.id))
            setConfirmDelete(null)
        } catch (err: any) {
            const msg = err?.response?.data?.detail || 'Failed to delete user.'
            setError(msg)
        } finally {
            setDeletingId(null)
        }
    }

    const openResetModal = (user: User) => {
        setResetTarget(user)
        setResetTempPassword(generateTempPassword())
        setResetError(null)
        setCopiedResetPassword(false)
    }

    const copyResetPassword = async () => {
        try {
            await navigator.clipboard.writeText(resetTempPassword)
            setCopiedResetPassword(true)
            setTimeout(() => setCopiedResetPassword(false), 2000)
        } catch {
            // ignore clipboard failures
        }
    }

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!resetTarget || !resetTempPassword.trim()) return
        try {
            setResetting(true)
            setResetError(null)
            await api.post(`/api/v1/users/${resetTarget.id}/reset-password`, {
                temp_password: resetTempPassword,
            })
            setResetTarget(null)
            setResetTempPassword('')
        } catch (err: any) {
            const msg =
                err?.response?.data?.detail ||
                'Failed to reset password. Cognito may be unavailable in local/dev mode.'
            setResetError(typeof msg === 'string' ? msg : 'Failed to reset password.')
        } finally {
            setResetting(false)
        }
    }

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
                    <p className="text-gray-500">Manage user roles and permissions</p>
                </div>
                <button
                    onClick={() => { setShowCreateModal(true); setCreateError(null) }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
                >
                    <UserPlus className="h-4 w-4" />
                    Create User
                </button>
            </div>

            {error && (
                <div className="bg-red-50 text-red-700 p-4 rounded-lg flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 flex-shrink-0" />
                    {error}
                    <button onClick={() => setError(null)} className="ml-auto"><X className="h-4 w-4" /></button>
                </div>
            )}

            {/* Users Table */}
            <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscription Tier</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {users.map((user) => (
                            <tr key={user.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                        <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                                            <Users className="h-5 w-5 text-primary-600" />
                                        </div>
                                        <div className="ml-4">
                                            <div className="text-sm font-medium text-gray-900">{user.email}</div>
                                            <div className="text-sm text-gray-500">ID: {user.id.slice(0, 8)}...</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <select
                                        value={normalizeRoleForUi(user.role)}
                                        onChange={(e) => handleRoleChange(user.id, e.target.value as Role)}
                                        disabled={updatingId === user.id}
                                        className={clsx(
                                            "block pl-3 pr-8 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500",
                                            ROLE_COLORS[normalizeRoleForUi(user.role)] ?? 'bg-white text-gray-900',
                                            updatingId === user.id && "opacity-50 cursor-not-allowed"
                                        )}
                                    >
                                        {ROLES.map(r => (
                                            <option key={r.value} value={r.value}>{r.label}</option>
                                        ))}
                                    </select>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={clsx(
                                        "px-2 inline-flex text-xs leading-5 font-semibold rounded-full",
                                        TIER_COLORS[user.subscription_tier] ?? 'bg-gray-100 text-gray-700'
                                    )}>
                                        {formatTierLabel(user.subscription_tier)}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={clsx(
                                        "px-2 inline-flex text-xs leading-5 font-semibold rounded-full",
                                        user.subscription_status === 'active'
                                            ? "bg-green-100 text-green-800"
                                            : "bg-gray-100 text-gray-800"
                                    )}>
                                        {user.subscription_status}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {new Date(user.created_at).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right">
                                    <div className="inline-flex items-center gap-1">
                                        <button
                                            onClick={() => openResetModal(user)}
                                            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-amber-700 hover:text-amber-900 hover:bg-amber-50 rounded transition-colors"
                                            title="Reset password"
                                        >
                                            <KeyRound className="h-3.5 w-3.5" />
                                            Reset password
                                        </button>
                                        <button
                                            onClick={() => setConfirmDelete(user)}
                                            disabled={deletingId === user.id}
                                            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors disabled:opacity-40"
                                            title="Delete user"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                            Delete
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {users.length === 0 && (
                            <tr>
                                <td colSpan={6} className="px-6 py-12 text-center text-gray-400 text-sm">
                                    No users found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Create User Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
                        <div className="flex items-center justify-between mb-5">
                            <h2 className="text-lg font-semibold text-gray-900">Create New User</h2>
                            <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <form onSubmit={handleCreateUser} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Email address <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="email"
                                    required
                                    value={createForm.email}
                                    onChange={e => setCreateForm({ ...createForm, email: e.target.value })}
                                    placeholder="user@company.com"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Temporary password <span className="text-red-500">*</span>
                                </label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        required
                                        value={createForm.temp_password}
                                        onChange={e => setCreateForm({ ...createForm, temp_password: e.target.value })}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setCreateForm({ ...createForm, temp_password: generateTempPassword() })}
                                        className="px-2 py-2 border border-gray-300 rounded-lg text-gray-500 hover:bg-gray-50 transition-colors"
                                        title="Generate new password"
                                    >
                                        <RefreshCw className="h-4 w-4" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={copyPassword}
                                        className="px-2 py-2 border border-gray-300 rounded-lg text-gray-500 hover:bg-gray-50 transition-colors"
                                        title="Copy password"
                                    >
                                        {copiedPassword
                                            ? <Check className="h-4 w-4 text-green-600" />
                                            : <Copy className="h-4 w-4" />}
                                    </button>
                                </div>
                                <p className="mt-1 text-xs text-gray-500">
                                    The user will be prompted to change this on first login.
                                    {' '}Copy it before submitting — it won't be shown again.
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                                <select
                                    value={createForm.role}
                                    onChange={e => setCreateForm({ ...createForm, role: e.target.value as Role })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                >
                                    {ROLES.map(r => (
                                        <option key={r.value} value={r.value}>{r.label}</option>
                                    ))}
                                </select>
                                <p className="mt-1 text-xs text-gray-500">
                                    {createForm.role === 'owner'       && 'Full access including billing and account settings.'}
                                    {createForm.role === 'admin'       && 'Manage users, agents, and all platform settings.'}
                                    {createForm.role === 'team_member' && 'Write access to create, edit, and deploy. Cannot manage users.'}
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Tier</label>
                                <select
                                    value={createForm.subscription_tier}
                                    onChange={e => setCreateForm({ ...createForm, subscription_tier: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                >
                                    <option value="free">Free</option>
                                    <option value="starter">Starter</option>
                                    <option value="professional">Professional</option>
                                    <option value="enterprise">Enterprise</option>
                                </select>
                            </div>

                            {createError && (
                                <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg flex items-center gap-2">
                                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                    {createError}
                                </div>
                            )}

                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowCreateModal(false)}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={creating}
                                    className="flex-1 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                                >
                                    {creating ? 'Creating…' : 'Create User'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Reset Password Modal (Owner/Admin) */}
            {resetTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
                        <div className="flex items-center justify-between mb-5">
                            <h2 className="text-lg font-semibold text-gray-900">Reset Password</h2>
                            <button
                                onClick={() => setResetTarget(null)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <p className="text-sm text-gray-600 mb-4">
                            Set a temporary password for{' '}
                            <span className="font-medium">{resetTarget.email}</span>. They must
                            choose a new password on next login (same flow as Create User).
                        </p>
                        <form onSubmit={handleResetPassword} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Temporary password <span className="text-red-500">*</span>
                                </label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        required
                                        minLength={8}
                                        value={resetTempPassword}
                                        onChange={e => setResetTempPassword(e.target.value)}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setResetTempPassword(generateTempPassword())}
                                        className="px-2 py-2 border border-gray-300 rounded-lg text-gray-500 hover:bg-gray-50 transition-colors"
                                        title="Generate new password"
                                    >
                                        <RefreshCw className="h-4 w-4" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={copyResetPassword}
                                        className="px-2 py-2 border border-gray-300 rounded-lg text-gray-500 hover:bg-gray-50 transition-colors"
                                        title="Copy password"
                                    >
                                        {copiedResetPassword
                                            ? <Check className="h-4 w-4 text-green-600" />
                                            : <Copy className="h-4 w-4" />}
                                    </button>
                                </div>
                                <p className="mt-1 text-xs text-gray-500">
                                    Copy it before submitting — it won't be shown again.
                                </p>
                            </div>

                            {resetError && (
                                <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg flex items-center gap-2">
                                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                    {resetError}
                                </div>
                            )}

                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setResetTarget(null)}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={resetting}
                                    className="flex-1 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
                                >
                                    {resetting ? 'Resetting…' : 'Reset Password'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirm Modal */}
            {confirmDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-2">Delete User?</h2>
                        <p className="text-sm text-gray-600 mb-5">
                            This will permanently remove <span className="font-medium">{confirmDelete.email}</span> and cannot be undone.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setConfirmDelete(null)}
                                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleDeleteUser(confirmDelete)}
                                disabled={deletingId === confirmDelete.id}
                                className="flex-1 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                            >
                                {deletingId === confirmDelete.id ? 'Deleting…' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
